import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { HERO_VIDEO, COMPANY } from "@/data/siteData";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="home" ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: videoY }} className="absolute inset-0 scale-110">
        <video
          className="w-full h-full object-cover"
          src={HERO_VIDEO}
          autoPlay
          muted
          loop
          playsInline
          data-testid="hero-video"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B132B]/70 via-[#0B132B]/40 to-[#0B132B]" />

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-center"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-teal-300 tracking-[0.35em] text-xs sm:text-sm uppercase mb-6"
          data-testid="hero-eyebrow"
        >
          DXHOLIDAYS · Navi Mumbai · Est. for wanderers
        </motion.p>

        <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl" data-testid="hero-headline">
          <Line delay={0.65}>Journeys crafted</Line>
          <Line delay={0.8}>
            like <span className="italic text-teal-300">poetry,</span>
          </Line>
          <Line delay={0.95}>
            priced on a <span className="italic text-amber-400">conversation.</span>
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.25 }}
          className="mt-7 max-w-xl text-base sm:text-lg text-slate-300"
          data-testid="hero-subtext"
        >
          Holiday packages, flight tickets and luxury bus bookings — curated by hand,
          quoted personally. Tell us where your heart points; we handle the rest.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.45 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollTo("packages")}
            data-testid="hero-explore-button"
            className="group rounded-full bg-teal-400 px-8 py-4 text-sm font-semibold tracking-wide text-[#0B132B] transition-transform duration-300 hover:scale-105 hover:bg-teal-300"
          >
            Explore Packages
          </button>
          <button
            onClick={() => scrollTo("contact")}
            data-testid="hero-contact-button"
            className="rounded-full border border-white/25 px-8 py-4 text-sm tracking-wide text-white backdrop-blur-sm transition-colors duration-300 hover:border-teal-300 hover:text-teal-300"
          >
            Contact for Price
          </button>
          <a
            href={COMPANY.phoneHref}
            data-testid="hero-call-link"
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-teal-300 transition-colors"
          >
            <Phone className="w-4 h-4" /> {COMPANY.phone}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-teal-300/70 animate-float-slow"
      >
        <ArrowDown className="w-5 h-5" />
      </motion.div>
    </section>
  );
};

export default Hero;
